export type PlaceCategory =
  | 'Nature & Wildlife'
  | 'Museums & Heritage'
  | 'Cultural & Historic'
  | 'Shopping & Lifestyle'
  | 'Art & Community'
  | 'City Spots & Views'
  | 'Dining'
  | 'Nightlife'
  | 'Adventure'
  | 'Family'
  | 'Festivals'

export interface Place {
  id: string
  name: string
  description: string
  category: PlaceCategory
  categories: PlaceCategory[]
  location: string
  neighborhood?: string
  image: string
  highlights: string[]
  info: {
    hours: string
    bestTime: string
    duration: string
    accessibility: string
  }
}

export const places: Place[] = [
  // NATURE & WILDLIFE (8)
  {
    id: '1',
    name: 'Nairobi National Park',
    description:
      'The only national park in the world within a capital city, offering incredible wildlife viewing with the city skyline as a backdrop.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Adventure', 'Family'],
    location: 'Langata Road',
    neighborhood: 'Langata',
    image:
      'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=80',
    highlights: [
      'Lions, rhinos, giraffes, and over 400 bird species',
      'Unique urban-wildlife experience',
      'Ivory Burning Site Monument',
      'Walking trails at hippo pools',
      'Photography with city backdrop',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:00 PM',
      bestTime: 'Early morning (6-9 AM) or late afternoon (4-6 PM)',
      duration: '4-5 hours for game drive',
      accessibility: 'Car access only, 4x4 recommended',
    },
  },
  {
    id: '2',
    name: 'David Sheldrick Wildlife Trust',
    description:
      'Elephant orphanage rescuing and rehabilitating baby elephants and rhinos. Daily public viewing hour offers intimate encounters with these magnificent creatures.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Family'],
    location: 'Nairobi National Park',
    neighborhood: 'Langata',
    image:
      'https://images.unsplash.com/photo-1564760290292-23341e4df6ec?w=800&q=80',
    highlights: [
      'Meet baby elephants and rhinos',
      'Learn about conservation efforts',
      'Adopt an elephant program',
      'Photography opportunities',
      'Educational talks by keepers',
    ],
    info: {
      hours: 'Daily 11:00 AM - 12:00 PM (viewing hour)',
      bestTime: 'Arrive by 10:45 AM for best viewing',
      duration: '1 hour',
      accessibility: 'Wheelchair accessible viewing area',
    },
  },
  {
    id: '3',
    name: 'Giraffe Centre',
    description:
      'Conservation center dedicated to endangered Rothschild giraffes, offering unique close encounters and educational programs.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Family'],
    location: 'Koitobos Road, Karen',
    neighborhood: 'Karen',
    image:
      'https://images.unsplash.com/photo-1564760055775-d63b17a55c44?w=800&q=80',
    highlights: [
      'Hand-feed endangered Rothschild giraffes',
      'Educational conservation exhibits',
      'Nature trail through indigenous forest',
      'Warthog sanctuary',
      'Photography from elevated platform',
    ],
    info: {
      hours: 'Daily 9:00 AM - 5:00 PM',
      bestTime: 'Weekday mornings (9-11 AM)',
      duration: '1-2 hours',
      accessibility: 'Fully wheelchair accessible',
    },
  },
  {
    id: '4',
    name: 'Ngong Hills',
    description:
      'Scenic hills offering hiking trails with panoramic views of Nairobi and the Great Rift Valley.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Adventure'],
    location: 'Ngong',
    neighborhood: 'Ngong',
    image:
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
    highlights: [
      'Four peaks hiking trail',
      'Panoramic Rift Valley views',
      'Wind farm',
      'Photography opportunities',
      'Picnic spots',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:00 PM',
      bestTime: 'Early morning or late afternoon',
      duration: '3-4 hours for full hike',
      accessibility: 'Not wheelchair accessible',
    },
  },
  {
    id: '5',
    name: 'Nairobi Arboretum',
    description:
      'Botanical garden with 350+ tree species, walking paths, and bird watching opportunities.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Family'],
    location: 'State House Road',
    neighborhood: 'Kilimani',
    image:
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
    highlights: [
      '350+ tree species',
      'Walking and jogging trails',
      'Bird watching',
      'Picnic areas',
      'Yoga sessions',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:00 PM',
      bestTime: 'Early morning for bird watching',
      duration: '1-2 hours',
      accessibility: 'Main paths accessible',
    },
  },
  {
    id: '6',
    name: 'Karura Forest',
    description:
      'Urban forest reserve with 50km of trails, waterfalls, caves, and diverse wildlife within the city.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Adventure', 'Family'],
    location: 'Limuru Road',
    neighborhood: 'Muthaiga',
    image:
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
    highlights: [
      '50km of walking and cycling trails',
      'Three beautiful waterfalls',
      'Mau Mau caves with historical significance',
      'Over 200 bird species',
      'Bike rental available',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:00 PM',
      bestTime: 'Early morning (6-9 AM) for bird watching',
      duration: '2-4 hours depending on trail',
      accessibility: 'Main trails wheelchair accessible',
    },
  },
  {
    id: '7',
    name: 'Oloolua Nature Trail',
    description:
      'Serene forest sanctuary with walking trails, waterfalls, and indigenous vegetation in Karen.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Adventure'],
    location: 'Karen',
    neighborhood: 'Karen',
    image:
      'https://images.unsplash.com/photo-1511497584788-876760111969?w=800&q=80',
    highlights: [
      'Peaceful forest trails',
      'Waterfall and caves',
      'Indigenous plant species',
      'Bird watching',
      'Picnic areas',
    ],
    info: {
      hours: 'Daily 7:00 AM - 6:00 PM',
      bestTime: 'Morning hours',
      duration: '2-3 hours',
      accessibility: 'Moderate terrain',
    },
  },
  {
    id: '8',
    name: 'Ngong Forest',
    description:
      'Indigenous forest with scenic trails, diverse flora and fauna, and peaceful natural environment.',
    category: 'Nature & Wildlife',
    categories: ['Nature & Wildlife', 'Adventure'],
    location: 'Ngong Road',
    neighborhood: 'Ngong',
    image:
      'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?w=800&q=80',
    highlights: [
      'Forest hiking trails',
      'Indigenous tree species',
      'Wildlife spotting',
      'Mountain biking routes',
      'Nature photography',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:00 PM',
      bestTime: 'Morning hours',
      duration: '2-3 hours',
      accessibility: 'Moderate trails',
    },
  },

  // MUSEUMS & HERITAGE SITES (9)
  {
    id: '9',
    name: 'Nairobi National Museum',
    description:
      "Kenya's flagship museum showcasing natural history, culture, contemporary art, and paleontological discoveries.",
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Cultural & Historic', 'Family'],
    location: 'Museum Hill',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?w=800&q=80',
    highlights: [
      'Great Hall of Kenya cultural exhibits',
      'Paleontology gallery with hominid fossils',
      'Contemporary East African art',
      'Snake park with live reptiles',
      'Botanical gardens',
    ],
    info: {
      hours: 'Daily 8:30 AM - 5:30 PM',
      bestTime: 'Weekday mornings (9-11 AM)',
      duration: '2-3 hours',
      accessibility: 'Fully wheelchair accessible',
    },
  },
  {
    id: '10',
    name: 'Karen Blixen Museum',
    description:
      'Historic farmhouse of "Out of Africa" author, preserved as museum showcasing colonial-era life.',
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Cultural & Historic'],
    location: 'Karen Road',
    neighborhood: 'Karen',
    image:
      'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800&q=80',
    highlights: [
      'Original farmhouse with period furniture',
      'Beautiful gardens and grounds',
      'Film location for "Out of Africa"',
      'Colonial artifacts collection',
      'Scenic Ngong Hills views',
    ],
    info: {
      hours: 'Daily 9:30 AM - 6:00 PM',
      bestTime: 'Mid-morning (10 AM-12 PM)',
      duration: '1.5-2 hours',
      accessibility: 'Ground floor and gardens accessible',
    },
  },
  {
    id: '11',
    name: 'Nairobi Railway Museum',
    description:
      "Historic railway museum showcasing Kenya's railway heritage with vintage locomotives and carriages.",
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Family'],
    location: 'Station Road',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=800&q=80',
    highlights: [
      'Vintage steam locomotives',
      'Historic railway carriages',
      'Uganda Railway history',
      'Interactive exhibits',
      'Photography opportunities',
    ],
    info: {
      hours: 'Daily 8:30 AM - 4:30 PM',
      bestTime: 'Mid-morning',
      duration: '1-2 hours',
      accessibility: 'Partially accessible',
    },
  },
  {
    id: '12',
    name: 'Bomas of Kenya',
    description:
      "Cultural center showcasing traditional villages, dances, and music from Kenya's diverse ethnic communities.",
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Cultural & Historic', 'Family'],
    location: 'Forest Edge Road, Langata',
    neighborhood: 'Langata',
    image:
      'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800&q=80',
    highlights: [
      'Traditional homesteads from 16+ ethnic groups',
      'Daily cultural dance performances',
      'Traditional music demonstrations',
      'Craft demonstrations',
      'Kenyan cuisine restaurant',
    ],
    info: {
      hours: 'Mon-Fri 8:00 AM - 5:00 PM | Sat-Sun 8:00 AM - 6:00 PM',
      bestTime: 'Arrive by 2:00 PM for 2:30 PM performance',
      duration: '3-4 hours',
      accessibility: 'Partially accessible',
    },
  },
  {
    id: '13',
    name: 'Maasai Market',
    description:
      'Rotating open-air market selling authentic Kenyan crafts, jewelry, textiles, and souvenirs.',
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Shopping & Lifestyle'],
    location: 'Various locations (rotates weekly)',
    neighborhood: 'Various',
    image:
      'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80',
    highlights: [
      'Authentic Maasai beadwork',
      'Hand-carved wooden sculptures',
      'Traditional textiles and fabrics',
      'Bargaining expected',
      'Unique souvenirs',
    ],
    info: {
      hours: 'Varies by location, typically 9:00 AM - 6:00 PM',
      bestTime: 'Mid-morning for best selection',
      duration: '1-2 hours',
      accessibility: 'Varies by location',
    },
  },
  {
    id: '14',
    name: 'Nairobi Gallery',
    description:
      'Contemporary art gallery showcasing East African artists in a historic colonial building.',
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Art & Community'],
    location: 'Kenyatta Avenue',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1577083552431-6e5fd01988ec?w=800&q=80',
    highlights: [
      'Rotating contemporary art exhibitions',
      'East African artists showcase',
      'Historic building',
      'Art workshops',
      'Cultural events',
    ],
    info: {
      hours: 'Tue-Sun 9:00 AM - 5:00 PM',
      bestTime: 'Weekday afternoons',
      duration: '1 hour',
      accessibility: 'Ground floor accessible',
    },
  },
  {
    id: '15',
    name: 'Kenya National Archives',
    description:
      "Repository of Kenya's historical documents, photographs, and artifacts chronicling the nation's journey.",
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Cultural & Historic'],
    location: 'Moi Avenue',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800&q=80',
    highlights: [
      'Historical documents and records',
      'Photographic archives',
      'Independence memorabilia',
      'Research facilities',
      'Educational exhibitions',
    ],
    info: {
      hours: 'Mon-Fri 8:00 AM - 5:00 PM',
      bestTime: 'Weekday mornings',
      duration: '1-2 hours',
      accessibility: 'Accessible',
    },
  },
  {
    id: '16',
    name: 'Snake Park',
    description:
      'Reptile sanctuary within the National Museum grounds featuring diverse snake species and educational programs.',
    category: 'Museums & Heritage',
    categories: ['Museums & Heritage', 'Nature & Wildlife', 'Family'],
    location: 'Museum Hill',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1531386151447-fd76ad50012f?w=800&q=80',
    highlights: [
      'Live snake exhibits',
      'Crocodile pool',
      'Tortoise sanctuary',
      'Educational talks',
      'Conservation programs',
    ],
    info: {
      hours: 'Daily 8:30 AM - 5:30 PM',
      bestTime: 'Mid-morning',
      duration: '1 hour',
      accessibility: 'Accessible',
    },
  },
  {
    id: '17',
    name: 'Uhuru Gardens',
    description:
      "National monument and memorial park commemorating Kenya's struggle for independence.",
    category: 'Museums & Heritage',
    categories: [
      'Museums & Heritage',
      'Cultural & Historic',
      'City Spots & Views',
    ],
    location: 'Langata Road',
    neighborhood: 'Langata',
    image:
      'https://images.unsplash.com/photo-1569098644584-210bcd375b59?w=800&q=80',
    highlights: [
      'Independence monuments',
      'Memorial gardens',
      'Historical significance',
      'Peaceful grounds',
      'National ceremonies venue',
    ],
    info: {
      hours: 'Daily 8:00 AM - 6:00 PM',
      bestTime: 'Morning or late afternoon',
      duration: '1-2 hours',
      accessibility: 'Wheelchair accessible',
    },
  },

  // CULTURAL & HISTORIC LANDMARKS (6)
  {
    id: '18',
    name: 'Jamia Mosque',
    description:
      'Beautiful historic mosque in the heart of Nairobi, showcasing Islamic architecture and serving the Muslim community.',
    category: 'Cultural & Historic',
    categories: ['Cultural & Historic'],
    location: 'Banda Street',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1564769610726-4b2c6c5b5e5e?w=800&q=80',
    highlights: [
      'Islamic architecture',
      'Historic significance',
      'Community center',
      'Beautiful interior',
      'Cultural landmark',
    ],
    info: {
      hours: 'Prayer times, visitors welcome outside prayer hours',
      bestTime: 'Morning hours',
      duration: '30 minutes',
      accessibility: 'Respectful dress required',
    },
  },
  {
    id: '19',
    name: 'Holy Family Basilica',
    description:
      'Stunning Catholic cathedral known for its distinctive architecture and spiritual significance.',
    category: 'Cultural & Historic',
    categories: ['Cultural & Historic'],
    location: 'Ngong Road',
    neighborhood: 'Kilimani',
    image:
      'https://images.unsplash.com/photo-1548625149-fc4a29cf7092?w=800&q=80',
    highlights: [
      'Modern architectural design',
      'Beautiful stained glass',
      'Spiritual center',
      'Community services',
      'Historic landmark',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:00 PM, Mass times vary',
      bestTime: 'Morning or afternoon',
      duration: '30-45 minutes',
      accessibility: 'Wheelchair accessible',
    },
  },
  {
    id: '20',
    name: 'Parliament Buildings',
    description:
      "Seat of Kenya's bicameral legislature featuring distinctive clock tower and colonial architecture.",
    category: 'Cultural & Historic',
    categories: ['Cultural & Historic'],
    location: 'Parliament Road',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=800&q=80',
    highlights: [
      'Colonial architecture',
      'Clock tower',
      'Seat of government',
      'Historical significance',
      'Guided tours available',
    ],
    info: {
      hours: 'Tours by appointment',
      bestTime: 'Weekday mornings',
      duration: '1-2 hours',
      accessibility: 'Limited access, security clearance required',
    },
  },
  {
    id: '21',
    name: 'Supreme Court of Kenya',
    description:
      "Impressive judicial building representing Kenya's legal system and constitutional democracy.",
    category: 'Cultural & Historic',
    categories: ['Cultural & Historic'],
    location: 'City Hall Way',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80',
    highlights: [
      'Modern architecture',
      'Judicial landmark',
      'Constitutional significance',
      'Public galleries',
      'Historic rulings venue',
    ],
    info: {
      hours: 'Court hours: Mon-Fri 8:00 AM - 5:00 PM',
      bestTime: 'Weekday mornings',
      duration: '1 hour',
      accessibility: 'Security screening required',
    },
  },
  {
    id: '22',
    name: 'Nairobi City Hall',
    description:
      'Historic colonial building serving as the seat of city government with distinctive architecture.',
    category: 'Cultural & Historic',
    categories: ['Cultural & Historic'],
    location: 'City Hall Way',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800&q=80',
    highlights: [
      'Colonial architecture',
      'City government seat',
      'Historic building',
      'Architectural landmark',
      'Public ceremonies venue',
    ],
    info: {
      hours: 'External viewing anytime',
      bestTime: 'Daytime hours',
      duration: '15-30 minutes',
      accessibility: 'External viewing accessible',
    },
  },
  {
    id: '23',
    name: 'Khoja Mosque',
    description:
      'Historic mosque serving the Khoja community, featuring traditional Islamic architecture.',
    category: 'Cultural & Historic',
    categories: ['Cultural & Historic'],
    location: 'Forest Road',
    neighborhood: 'Parklands',
    image:
      'https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=800&q=80',
    highlights: [
      'Traditional architecture',
      'Community center',
      'Historic significance',
      'Cultural landmark',
      'Beautiful interior',
    ],
    info: {
      hours: 'Prayer times, visitors welcome outside prayer hours',
      bestTime: 'Morning hours',
      duration: '30 minutes',
      accessibility: 'Respectful dress required',
    },
  },

  // SHOPPING, LIFESTYLE & ENTERTAINMENT (10)
  {
    id: '24',
    name: 'Westlands',
    description:
      'Vibrant entertainment district with restaurants, bars, clubs, and shopping centers.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Nightlife', 'Dining'],
    location: 'Westlands',
    neighborhood: 'Westlands',
    image:
      'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800&q=80',
    highlights: [
      'Diverse dining options',
      'Nightlife hotspot',
      'Shopping centers',
      'Entertainment venues',
      'Cosmopolitan atmosphere',
    ],
    info: {
      hours: 'Varies by venue',
      bestTime: 'Evenings and weekends',
      duration: '3-5 hours',
      accessibility: 'Most venues accessible',
    },
  },
  {
    id: '25',
    name: 'Village Market',
    description:
      'Upscale shopping mall with international brands, restaurants, cinema, and entertainment.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Dining', 'Family'],
    location: 'Limuru Road, Gigiri',
    neighborhood: 'Gigiri',
    image:
      'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800&q=80',
    highlights: [
      'International brand stores',
      'Multiple restaurants and cafes',
      'Cinema complex',
      'Kids play area',
      'Weekly Maasai Market',
    ],
    info: {
      hours: 'Daily 9:00 AM - 9:00 PM',
      bestTime: 'Weekdays less crowded',
      duration: '2-4 hours',
      accessibility: 'Fully wheelchair accessible',
    },
  },
  {
    id: '26',
    name: 'Two Rivers Mall',
    description:
      "Modern mega-mall featuring shopping, dining, entertainment, and Africa's largest waterfront.",
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Dining', 'Family'],
    location: 'Limuru Road',
    neighborhood: 'Ruaka',
    image:
      'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=800&q=80',
    highlights: [
      'Largest mall in East Africa',
      'Waterfront dining',
      'Cinema and entertainment',
      'International brands',
      'Family attractions',
    ],
    info: {
      hours: 'Daily 9:00 AM - 10:00 PM',
      bestTime: 'Weekdays',
      duration: '3-5 hours',
      accessibility: 'Fully wheelchair accessible',
    },
  },
  {
    id: '27',
    name: 'Sarit Centre',
    description:
      'Established shopping center with diverse retail stores, supermarket, and dining options.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Dining'],
    location: 'Westlands',
    neighborhood: 'Westlands',
    image:
      'https://images.unsplash.com/photo-1567958451986-2de427a4a0be?w=800&q=80',
    highlights: [
      'Diverse retail stores',
      'Large supermarket',
      'Multiple restaurants',
      'Banking services',
      'Ample parking',
    ],
    info: {
      hours: 'Daily 9:00 AM - 9:00 PM',
      bestTime: 'Weekday mornings',
      duration: '2-3 hours',
      accessibility: 'Wheelchair accessible',
    },
  },
  {
    id: '28',
    name: 'Yaya Centre',
    description:
      'Popular shopping center in Kilimani with retail stores, restaurants, and services.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Dining'],
    location: 'Argwings Kodhek Road',
    neighborhood: 'Kilimani',
    image:
      'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=800&q=80',
    highlights: [
      'Retail shopping',
      'Restaurants and cafes',
      'Supermarket',
      'Banking services',
      'Central location',
    ],
    info: {
      hours: 'Daily 9:00 AM - 9:00 PM',
      bestTime: 'Weekdays',
      duration: '1-2 hours',
      accessibility: 'Wheelchair accessible',
    },
  },
  {
    id: '29',
    name: 'The Hub Karen',
    description:
      'Boutique shopping center in Karen with specialty stores, cafes, and artisan shops.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Dining'],
    location: 'Karen',
    neighborhood: 'Karen',
    image:
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80',
    highlights: [
      'Boutique stores',
      'Artisan products',
      'Specialty cafes',
      'Outdoor seating',
      'Relaxed atmosphere',
    ],
    info: {
      hours: 'Daily 9:00 AM - 8:00 PM',
      bestTime: 'Weekends',
      duration: '1-2 hours',
      accessibility: 'Accessible',
    },
  },
  {
    id: '30',
    name: 'Garden City Mall',
    description:
      'Modern shopping and lifestyle destination with retail, dining, and entertainment options.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Dining', 'Family'],
    location: 'Thika Road',
    neighborhood: 'Ruaraka',
    image:
      'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=800&q=80',
    highlights: [
      'Modern architecture',
      'Diverse retail options',
      'Multiple restaurants',
      'Cinema',
      'Family-friendly',
    ],
    info: {
      hours: 'Daily 9:00 AM - 10:00 PM',
      bestTime: 'Weekdays',
      duration: '2-4 hours',
      accessibility: 'Fully wheelchair accessible',
    },
  },
  {
    id: '31',
    name: 'Ngong Road Forest Sanctuary',
    description:
      'Adventure park offering ziplining, nature walks, and outdoor activities in urban forest setting.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Adventure', 'Nature & Wildlife'],
    location: 'Ngong Road',
    neighborhood: 'Kilimani',
    image:
      'https://images.unsplash.com/photo-1527004013197-933c4bb611b3?w=800&q=80',
    highlights: [
      'Zipline adventure',
      'Forest trails',
      'Team building activities',
      'Nature walks',
      'Picnic areas',
    ],
    info: {
      hours: 'Daily 9:00 AM - 6:00 PM',
      bestTime: 'Morning hours',
      duration: '2-3 hours',
      accessibility: 'Moderate physical activity required',
    },
  },
  {
    id: '32',
    name: 'The Alchemist',
    description:
      'Multi-level entertainment complex with bars, restaurants, live music, and rooftop terrace.',
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Nightlife', 'Dining'],
    location: 'Muthangari Drive, Westlands',
    neighborhood: 'Westlands',
    image:
      'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=800&q=80',
    highlights: [
      'Multiple bars and restaurants',
      'Live music and DJs',
      'Rooftop bar',
      'Art gallery',
      'Trendy atmosphere',
    ],
    info: {
      hours: 'Daily 11:00 AM - 2:00 AM',
      bestTime: 'Thursday-Saturday evenings',
      duration: '3-5 hours',
      accessibility: 'Elevator access to all floors',
    },
  },
  {
    id: '33',
    name: 'K1 Klubhouse',
    description:
      "Upscale nightclub and lounge popular with Nairobi's elite, featuring premium entertainment.",
    category: 'Shopping & Lifestyle',
    categories: ['Shopping & Lifestyle', 'Nightlife'],
    location: 'Parklands',
    neighborhood: 'Parklands',
    image:
      'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=800&q=80',
    highlights: [
      'VIP experiences',
      'International DJs',
      'Upscale atmosphere',
      'Premium entertainment',
      'Late night venue',
    ],
    info: {
      hours: 'Wed-Sun 9:00 PM - 4:00 AM',
      bestTime: 'Friday and Saturday nights',
      duration: '4-6 hours',
      accessibility: 'Limited accessibility',
    },
  },

  // ART, URBAN & COMMUNITY SPACES (6)
  {
    id: '34',
    name: 'GoDown Arts Centre',
    description:
      'Contemporary arts space hosting exhibitions, performances, and creative workshops.',
    category: 'Art & Community',
    categories: ['Art & Community', 'Museums & Heritage'],
    location: 'Dunga Road, Industrial Area',
    neighborhood: 'Industrial Area',
    image:
      'https://images.unsplash.com/photo-1577083552431-6e5fd01988ec?w=800&q=80',
    highlights: [
      'Contemporary art exhibitions',
      'Live performances',
      'Artist studios',
      'Creative workshops',
      'Cultural events',
    ],
    info: {
      hours: 'Mon-Fri 9:00 AM - 5:00 PM',
      bestTime: 'Check event schedule',
      duration: '1-2 hours',
      accessibility: 'Wheelchair accessible',
    },
  },
  {
    id: '35',
    name: 'Kuona Trust Art Centre',
    description:
      'Artist residency and gallery space supporting contemporary East African artists.',
    category: 'Art & Community',
    categories: ['Art & Community'],
    location: 'Hurlingham',
    neighborhood: 'Hurlingham',
    image:
      'https://images.unsplash.com/photo-1577083552431-6e5fd01988ec?w=800&q=80',
    highlights: [
      'Artist studios',
      'Gallery exhibitions',
      'Creative workshops',
      'Art sales',
      'Community programs',
    ],
    info: {
      hours: 'Mon-Fri 9:00 AM - 5:00 PM',
      bestTime: 'Weekday afternoons',
      duration: '1 hour',
      accessibility: 'Accessible',
    },
  },
  {
    id: '36',
    name: 'Circle Art Gallery',
    description:
      'Contemporary art gallery showcasing established and emerging East African artists.',
    category: 'Art & Community',
    categories: ['Art & Community'],
    location: 'Parklands',
    neighborhood: 'Parklands',
    image:
      'https://images.unsplash.com/photo-1577083552431-6e5fd01988ec?w=800&q=80',
    highlights: [
      'Contemporary art',
      'Rotating exhibitions',
      'Artist talks',
      'Art sales',
      'Cultural events',
    ],
    info: {
      hours: 'Tue-Sun 10:00 AM - 6:00 PM',
      bestTime: 'Weekday afternoons',
      duration: '1 hour',
      accessibility: 'Accessible',
    },
  },
  {
    id: '37',
    name: 'iHub Nairobi',
    description:
      'Innovation hub and tech community space fostering entrepreneurship and collaboration.',
    category: 'Art & Community',
    categories: ['Art & Community'],
    location: 'Ngong Road',
    neighborhood: 'Kilimani',
    image:
      'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    highlights: [
      'Tech community',
      'Coworking space',
      'Innovation programs',
      'Networking events',
      'Startup support',
    ],
    info: {
      hours: 'Mon-Fri 8:00 AM - 6:00 PM',
      bestTime: 'Weekdays',
      duration: 'Varies',
      accessibility: 'Accessible',
    },
  },
  {
    id: '38',
    name: 'Ngara Community Arts Centres',
    description:
      'Grassroots arts spaces supporting local artists and community cultural programs.',
    category: 'Art & Community',
    categories: ['Art & Community'],
    location: 'Ngara',
    neighborhood: 'Ngara',
    image:
      'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=800&q=80',
    highlights: [
      'Community art programs',
      'Local artists',
      'Cultural workshops',
      'Youth programs',
      'Grassroots initiatives',
    ],
    info: {
      hours: 'Varies by center',
      bestTime: 'Weekdays',
      duration: '1-2 hours',
      accessibility: 'Varies',
    },
  },
  {
    id: '39',
    name: 'Kibera Art District',
    description:
      'Vibrant arts community in Kibera showcasing street art, murals, and local creativity.',
    category: 'Art & Community',
    categories: ['Art & Community'],
    location: 'Kibera',
    neighborhood: 'Kibera',
    image:
      'https://images.unsplash.com/photo-1499781350541-7783f6c6a0c8?w=800&q=80',
    highlights: [
      'Street art and murals',
      'Community artists',
      'Cultural tours',
      'Local creativity',
      'Social impact art',
    ],
    info: {
      hours: 'Daytime hours recommended',
      bestTime: 'Morning with guide',
      duration: '2-3 hours',
      accessibility: 'Guided tours recommended',
    },
  },

  // ICONIC CITY SPOTS & VIEWS (6)
  {
    id: '40',
    name: 'Uhuru Park',
    description:
      'Central park and green space in the heart of the city, popular for picnics, jogging, and relaxation.',
    category: 'City Spots & Views',
    categories: ['City Spots & Views', 'Nature & Wildlife', 'Family'],
    location: 'Uhuru Highway',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
    highlights: [
      'Large open green spaces',
      'Uhuru Monument',
      'Boating on artificial lake',
      'Jogging trails',
      'City skyline views',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:30 PM',
      bestTime: 'Early morning or late afternoon',
      duration: '1-2 hours',
      accessibility: 'Wheelchair accessible paths',
    },
  },
  {
    id: '41',
    name: 'Central Park',
    description:
      'Urban park offering green space, walking paths, and recreational areas in the city center.',
    category: 'City Spots & Views',
    categories: ['City Spots & Views', 'Nature & Wildlife'],
    location: 'Uhuru Highway',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
    highlights: [
      'Green space',
      'Walking paths',
      'City views',
      'Relaxation area',
      'Urban oasis',
    ],
    info: {
      hours: 'Daily 6:00 AM - 6:30 PM',
      bestTime: 'Morning or afternoon',
      duration: '1 hour',
      accessibility: 'Accessible',
    },
  },
  {
    id: '42',
    name: 'Nyayo Stadium',
    description:
      'National stadium hosting sports events, concerts, and major public gatherings.',
    category: 'City Spots & Views',
    categories: ['City Spots & Views'],
    location: 'Langata Road',
    neighborhood: 'Nairobi West',
    image:
      'https://images.unsplash.com/photo-1459865264687-595d652de67e?w=800&q=80',
    highlights: [
      'Sports events',
      'Concerts and shows',
      'National gatherings',
      'Athletic facilities',
      'Cultural events',
    ],
    info: {
      hours: 'Event-dependent',
      bestTime: 'Check event schedule',
      duration: 'Varies by event',
      accessibility: 'Accessible seating available',
    },
  },
  {
    id: '43',
    name: 'KICC Rooftop Viewpoint',
    description:
      'Iconic cylindrical tower offering 360° panoramic views of Nairobi from the helipad viewing platform.',
    category: 'City Spots & Views',
    categories: ['City Spots & Views', 'Cultural & Historic'],
    location: 'Harambee Avenue',
    neighborhood: 'CBD',
    image:
      'https://images.unsplash.com/photo-1611348524140-53c9a25263d6?w=800&q=80',
    highlights: [
      'Panoramic 360° city views',
      'Iconic architecture',
      'Helipad viewing platform',
      'Photography opportunities',
      'Historical significance',
    ],
    info: {
      hours: 'Daily 8:00 AM - 6:00 PM',
      bestTime: 'Late afternoon for sunset views',
      duration: '1 hour',
      accessibility: 'Elevator to viewing deck',
    },
  },
  {
    id: '44',
    name: 'Ngong Road Viewpoints',
    description:
      'Scenic viewpoints along Ngong Road offering stunning vistas of the city and surrounding landscape.',
    category: 'City Spots & Views',
    categories: ['City Spots & Views', 'Nature & Wildlife'],
    location: 'Ngong Road',
    neighborhood: 'Kilimani',
    image:
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
    highlights: [
      'City panoramas',
      'Sunset views',
      'Photography spots',
      'Scenic drives',
      'Natural beauty',
    ],
    info: {
      hours: 'Always accessible',
      bestTime: 'Late afternoon for sunset',
      duration: '30 minutes - 1 hour',
      accessibility: 'Roadside viewing',
    },
  },
  {
    id: '45',
    name: 'Upper Hill Skyline Views',
    description:
      'Modern business district offering impressive skyline views and contemporary architecture.',
    category: 'City Spots & Views',
    categories: ['City Spots & Views'],
    location: 'Upper Hill',
    neighborhood: 'Upper Hill',
    image:
      'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800&q=80',
    highlights: [
      'Modern skyline',
      'Contemporary architecture',
      'Business district',
      'City development showcase',
      'Photography opportunities',
    ],
    info: {
      hours: 'Always accessible',
      bestTime: 'Daytime or evening',
      duration: '30 minutes',
      accessibility: 'Accessible',
    },
  },
]

export const categories: PlaceCategory[] = [
  'Nature & Wildlife',
  'Museums & Heritage',
  'Cultural & Historic',
  'Shopping & Lifestyle',
  'Art & Community',
  'City Spots & Views',
  'Dining',
  'Nightlife',
  'Adventure',
  'Family',
  'Festivals',
]

export function getRelatedPlaces(
  currentPlaceId: string,
  limit: number = 3,
): Place[] {
  const currentPlace = places.find((p) => p.id === currentPlaceId)
  if (!currentPlace) return []

  return places
    .filter((p) => p.id !== currentPlaceId)
    .filter((p) =>
      p.categories.some((cat) => currentPlace.categories.includes(cat)),
    )
    .slice(0, limit)
}

export function getPlacesByNeighborhood(neighborhood: string): Place[] {
  return places.filter((p) => p.neighborhood === neighborhood)
}

export function getPlacesByCategory(category: PlaceCategory): Place[] {
  return places.filter((p) => p.categories.includes(category))
}
